import API_CONFIG, { getApiUrl } from '../../../config/api';
import { clients, HttpError } from '@/lib/https';
import { useAuthStore } from '@/state/store/auth';
import {
  AccountActivationPayload,
  ForgotPasswordPayload,
  ForgotPasswordResponse,
  SigninEmailPayload,
  SigninEmailResponse,
} from '../types/auth.type';

/**
 * Authentication API utilities — v4 endpoints only.
 *
 * The v1 endpoints (/authentication/v1/* and /iam/v1/*) live on the legacy
 * global API host and are not available on the project-scoped host
 * (https://blocksapi.<cookieDomain>). All calls below use v4 (verified live
 * via curl against blocksapi.slsblx.com):
 *
 *   signin           → POST /iam/v4/auth/login      (JSON body)
 *   signout          → POST /iam/v4/auth/Logout      (body {})
 *   getRefreshToken  → POST /iam/v4/oidc/token       (form-encoded)
 *   accountActivation → POST /iam/v4/auth/activate   (JSON body)
 *
 * For the rest (forgotPassword / resetPassword / resendActivation /
 * logoutAll), the v4 path layout mirrors v1 with `/iam/v4/auth/<verb>`.
 * If the canonical name differs on your project, update here.
 */

const JSON_HEADERS = () => ({
  'Content-Type': 'application/json',
  'x-blocks-key': API_CONFIG.blocksKey,
});

const FORM_HEADERS = () => ({
  'Content-Type': 'application/x-www-form-urlencoded',
  'x-blocks-key': API_CONFIG.blocksKey,
});

export interface SignInResponse {
  access_token: string;
  refresh_token: string;
  enable_mfa: boolean;
  mfaId: string;
  mfaType: number;
}

export type PasswordSigninPayload = {
  grantType: 'password';
  username: string;
  password: string;
  captchaToken?: string;
};

export type SSoSigninPayload = {
  grantType: 'social';
  code: string;
  state: string;
};

export type MFASigninPayload = {
  grantType: 'mfa_code';
  code: string;
  mfaId: string;
  mfaType: number;
};

export type MFASigninResponse = {
  access_token: string;
  refresh_token: string;
};

export interface SigninBySSOPayload {
  grantType: 'social';
  code: string;
  state: string;
}

// ---------------------------------------------------------------------------
// v4 sign-in (POST /iam/v4/auth/login, JSON body)
// ---------------------------------------------------------------------------
export const signin = async <T extends 'password' | 'social' | 'mfa_code' = 'password'>(
  payload: PasswordSigninPayload | MFASigninPayload | SigninBySSOPayload
): Promise<T extends 'password' | 'social' ? SignInResponse : MFASigninResponse> => {
  const url = getApiUrl('/iam/v4/auth/login');

  // v4 password / SSO / MFA all share the JSON body shape — distinguished
  // by `grantType`. The server returns the same token envelope.
  const response = await fetch(url, {
    method: 'POST',
    headers: JSON_HEADERS(),
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new HttpError(response.status, err);
  }
  return response.json();
};

// ---------------------------------------------------------------------------
// v4 sign-out (cookie/session, body `{}`)
// ---------------------------------------------------------------------------
export const signout = async (): Promise<{ isSuccess: true }> => {
  localStorage.removeItem('auth-storage');
  try {
    return await clients.post('/iam/v4/auth/Logout', JSON.stringify({}));
  } catch (error) {
    // 401 means "no active session to revoke" — that's a successful sign-out
    // from the user's perspective, not an error. Swallow it.
    if (error instanceof HttpError && error.status === 401) {
      return { isSuccess: true };
    }
    console.error('Logout operation failed:', error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
// v4 refresh (POST /iam/v4/oidc/token, form-encoded)
//
// Requires VITE_BLOCKS_OIDC_CLIENT_ID (the OIDC client configured in the
// Blocks portal). When the project doesn't have SSO configured, refresh is
// a no-op — the auth middleware will sign the user out instead.
// ---------------------------------------------------------------------------
export const getRefreshToken = async (): Promise<null | {
  access_token?: string;
  refresh_token?: string;
}> => {
  const clientId = (import.meta.env.VITE_BLOCKS_OIDC_CLIENT_ID as string) ?? '';
  if (!clientId) {
    // No OIDC client configured → can't refresh; signal caller to sign out.
    return null;
  }
  const url = getApiUrl('/iam/v4/oidc/token');
  const formData = new URLSearchParams();
  formData.append('grant_type', 'refresh_token');
  formData.append('client_id', clientId);
  const stored = useAuthStore.getState().refreshToken;
  if (stored) formData.append('refresh_token', stored);

  const response = await fetch(url, {
    method: 'POST',
    body: formData,
    headers: FORM_HEADERS(),
    credentials: 'include',
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new HttpError(response.status, err);
  }
  return response.json();
};

// ---------------------------------------------------------------------------
// v4 account activation
// ---------------------------------------------------------------------------
export const accountActivation = async (data: AccountActivationPayload) => {
  const payload = {
    ...data,
    captchaCode: '',
    mailPurpose: '',
    preventPostEvent: true,
  };
  return clients.post('/iam/v4/auth/activate', JSON.stringify(payload));
};

// ---------------------------------------------------------------------------
// v4 forgot / reset password
// ---------------------------------------------------------------------------
export const forgotPassword = async (
  payload: ForgotPasswordPayload
): Promise<ForgotPasswordResponse> => {
  const modified = {
    ...payload,
    mailPurpose: 'RecoverAccount',
  };
  return clients.post('/iam/v4/auth/forgot-password', JSON.stringify(modified));
};

export const resetPassword = async (data: { code: string; password: string }) => {
  const payload = {
    ...data,
    logoutFromAllDevices: true,
    ProjectKey: API_CONFIG.blocksKey,
  };
  return clients.post('/iam/v4/auth/reset-password', JSON.stringify(payload));
};

export const resendActivation = async (data: { userId: string }) => {
  const payload = {
    ...data,
    mailPurpose: 'ResendActivation',
  };
  return clients.post('/iam/v4/auth/resend-activation', JSON.stringify(payload));
};

// ---------------------------------------------------------------------------
// v4 logout-all
// ---------------------------------------------------------------------------
export const logoutAll = async () => {
  return clients.post('/iam/v4/auth/logout-all', '');
};

// ---------------------------------------------------------------------------
// v4 email/password sign-in (used by /signin-email component)
// ---------------------------------------------------------------------------
export const signinByEmail = (
  payload: SigninEmailPayload
): Promise<SigninEmailResponse> => {
  return clients.post('/iam/v4/auth/login', JSON.stringify(payload), JSON_HEADERS());
};